<template>
  <div class="skeleton-horizontal-list">
    <!-- Section Title -->
    <div class="skeleton-section-header">
      <div class="skeleton-header-content">
        <div class="skeleton-section-title">
          <div class="skeleton-shimmer"></div>
        </div>
      </div>
    </div>

    <!-- Skeleton Game Cards Container -->
    <div class="skeleton-games-container">
      <div class="skeleton-games-wrapper">
        <div
          v-for="index in skeletonCardCount"
          :key="`skeleton-${index}`"
          class="skeleton-card-wrapper"
        >
          <SkeletonGameCard />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SkeletonGameCard from './SkeletonGameCard.vue'

export default {
  name: 'SkeletonHorizontalList',
  components: {
    SkeletonGameCard
  },
  data() {
    return {
      skeletonCardCount: 8 // Show 8 skeleton cards initially
    }
  },
  mounted() {
    // Adjust skeleton count based on screen size
    this.adjustSkeletonCount()
    window.addEventListener('resize', this.adjustSkeletonCount)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.adjustSkeletonCount)
  },
  methods: {
    adjustSkeletonCount() {
      const viewportWidth = window.innerWidth
      if (viewportWidth <= 480) {
        this.skeletonCardCount = 6
      } else if (viewportWidth <= 768) {
        this.skeletonCardCount = 7
      } else {
        this.skeletonCardCount = 8
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.skeleton-horizontal-list {
  width: 100%;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }

  @media (max-width: 480px) {
    margin-bottom: 25px;
  }
}

.skeleton-section-header {
  padding: 0 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    padding: 0 15px;
    margin-bottom: 12px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
    margin-bottom: 10px;
  }
}

.skeleton-header-content {
  display: flex;
  align-items: center;
  width: 100%;
}

.skeleton-section-title {
  position: relative;
  width: 280px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;

  @media (max-width: 768px) {
    width: 240px;
    height: 28px;
  }

  @media (max-width: 480px) {
    width: 200px;
    height: 24px;
  }
}

.skeleton-games-container {
  padding: 0 20px;
  min-height: 280px;

  @media (max-width: 768px) {
    padding: 0 15px;
    min-height: 250px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
    min-height: 220px;
  }
}

.skeleton-games-wrapper {
  display: flex;
  gap: 8px;
  overflow-x: hidden;
  padding-bottom: 10px;

  // Match HorizontalGameLists scroll behavior
  scroll-behavior: smooth;

  // Performance optimizations
  will-change: transform;
  transform: translate3d(0, 0, 0);

  @media (max-width: 768px) {
    gap: 6px;
  }

  @media (max-width: 480px) {
    gap: 5px;
  }
}

.skeleton-card-wrapper {
  flex-shrink: 0;
  width: 180px;

  @media (max-width: 768px) {
    width: 160px;
  }

  @media (max-width: 480px) {
    width: 140px;
  }
}

// Shimmer animation
.skeleton-shimmer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.08),
    transparent
  );
  animation: shimmer 2.5s infinite;
  border-radius: inherit;
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

// Responsive adjustments to match HorizontalGameLists
@media (max-width: 768px) {
  .skeleton-horizontal-list {
    .skeleton-section-title {
      height: 26px;
    }
  }
}

@media (max-width: 480px) {
  .skeleton-horizontal-list {
    .skeleton-section-title {
      height: 22px;
    }
  }
}

// Performance optimizations
.skeleton-games-wrapper {
  contain: layout style paint;
}
</style>
